import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, Search, Bell, MoreVertical, 
  Shield, CheckCircle, Mail, MapPin, 
  AlertTriangle, Globe, Music, FileText,
  DollarSign, Clock, Zap
} from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { siteConfig } from "../data/mock";

const IntakePage = () => {
  // SEO: No Index — Intake portal must be invisible to search engines
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = "robots";
    metaRobots.content = "noindex, nofollow";
    document.head.appendChild(metaRobots);

    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pb-20 relative overflow-hidden">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      {/* Top Mobile Bar (Mocked for UI matching) */}
      <div className="sticky top-0 z-50 bg-[rgb(17,17,19)]/90 backdrop-blur-md border-b border-[rgba(255,255,255,0.06)] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <Link to="/" className="text-[rgb(161,161,170)] hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <div className="bg-[rgb(26,28,30)] rounded-full px-4 py-1.5 flex items-center gap-2 flex-1 max-w-xs border border-[rgba(255,255,255,0.05)]">
            <Globe size={12} className="text-[rgb(100,100,110)]" />
            <span className="text-[rgb(161,161,170)] text-xs truncate">itzgreenmusic.com/intake</span>
          </div>
        </div>
        <div className="flex items-center gap-4 pl-4">
          <div className="relative">
            <Bell size={20} className="text-[rgb(161,161,170)]" />
            <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full text-[8px] flex items-center justify-center font-bold text-white border border-[rgb(17,17,19)]">
              2
            </div>
          </div>
          <MoreVertical size={20} className="text-[rgb(161,161,170)]" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 relative z-10 pt-6">
        
        {/* Campaign Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 mb-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs"
        >
          <div className="flex items-center gap-2">
            <DollarSign size={14} className="text-[rgb(218,255,1)]" />
            <span className="text-white font-bold tracking-wide">CAMPAIGN ($60K ALL-IN)</span>
          </div>
          <div className="h-3 w-px bg-[rgba(255,255,255,0.1)] hidden sm:block" />
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-amber-500" />
            <span className="text-amber-500 font-bold tracking-wide">DEADLINE: MARCH 28</span>
          </div>
          <div className="h-3 w-px bg-[rgba(255,255,255,0.1)] hidden sm:block" />
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-red-500" />
            <span className="text-red-500 font-bold tracking-wide">Q3/Q4 BROADCAST SLATES</span>
          </div>
          <p className="text-[rgb(100,100,110)] w-full mt-1 sm:w-auto sm:mt-0 sm:ml-auto text-[10px] uppercase tracking-wider">
            (HIGH-TENSION & CINEMATIC HORROR | TARGETING: BLUMHOUSE & TRAILER PARKS)
          </p>
        </motion.div>

        {/* Main Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-10 text-center sm:text-left"
        >
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2 justify-center sm:justify-start">
                <CheckCircle size={16} className="text-[rgb(218,255,1)]" />
                <span className="text-[rgb(218,255,1)] text-[10px] tracking-[0.2em] font-bold">
                  ITZGREEN ADMIN
                </span>
                <span className="text-[rgb(100,100,110)] text-[10px]">|</span>
                <span className="text-[rgb(161,161,170)] text-[10px] tracking-[0.1em]">
                  GLOBAL SYNC CLEARANCE
                </span>
              </div>
              <h1 className="text-white text-2xl sm:text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                EXECUTIVE PRODUCED BY LEAVER HALL.<br />
                <span className="text-[rgb(218,255,1)]">PRE-CLEARED FOR GLOBAL SYNC.</span>
              </h1>
            </div>
            
            <Link
              to="/ingestion"
              className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-bold text-xs tracking-[0.15em] px-6 py-3 rounded-lg hover:bg-[rgb(166,190,21)] transition-colors shadow-[0_0_20px_rgba(218,255,1,0.3)] whitespace-nowrap"
            >
              JOIN THE ROSTER
            </Link>
          </div>

          <div className="flex flex-wrap justify-center sm:justify-start gap-4 sm:gap-6 text-[10px] tracking-[0.15em] text-[rgb(161,161,170)] font-medium">
            <Link to="/catalog" className="hover:text-white transition-colors">CATALOG</Link>
            <Link to="/roster" className="hover:text-white transition-colors">ROSTER</Link>
            <Link to="/about" className="hover:text-white transition-colors">ABOUT</Link>
            <Link to="/licensing" className="hover:text-white transition-colors">LICENSING</Link>
            <Link to="/vault" className="text-[rgb(218,255,1)] hover:underline">VAULT</Link>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Left Column: Company Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="md:col-span-2 space-y-6"
          >
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[rgb(218,255,1)] flex items-center justify-center text-[rgb(17,17,19)] font-bold font-mono">
                  IG
                </div>
                <h3 className="text-white font-bold tracking-wide" style={{ fontFamily: "'Oswald', sans-serif" }}>ITZGREEN MUSIC</h3>
              </div>
              
              <p className="text-[rgb(161,161,170)] text-sm leading-relaxed mb-6 border-b border-[rgba(255,255,255,0.06)] pb-6">
                Boutique sync catalog providing one-stop licensing solutions for film, television, and streaming platforms worldwide.
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-xs">
                  <Shield size={14} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(218,218,218)]">PRO: <span className="text-white font-mono">BMI</span> | Publisher IPI: <span className="text-white font-mono">01323361389</span></span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <FileText size={14} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(218,218,218)]">Mechanicals: <span className="text-white">Approved Member of The MLC</span></span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <Music size={14} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(218,218,218)]">SoundExchange Rights Owner (Payee: <span className="text-white font-mono">2181132146</span>)</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <MapPin size={14} className="text-[rgb(218,255,1)]" />
                  <span className="text-[rgb(218,218,218)]">HQ: <span className="text-white">Hoboken, NJ</span></span>
                </div>
              </div>
            </div>

            {/* Legal Notice */}
            <div className="bg-[rgba(234,179,8,0.05)] border border-yellow-500/20 rounded-xl p-5 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-yellow-500/50" />
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-yellow-500 text-xs font-bold tracking-widest mb-2">LEGAL NOTICE</h4>
                  <p className="text-[rgb(200,200,200)] text-xs leading-relaxed">
                    ItzGreenMusic operates a strict <span className="text-white font-medium">zero-tolerance policy</span> for generative AI and uncleared samples. By submitting audio to this portal, the sender legally warrants that all materials are 100% human-composed and free of copyright infringement.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Contact */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-6">
              <h3 className="text-white font-bold tracking-widest text-xs mb-6 border-b border-[rgba(255,255,255,0.06)] pb-2">CONTACT</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-[rgb(100,100,110)] text-[10px] tracking-wider block mb-1">DIRECT A&R LINE</label>
                  <a href="mailto:leaver@itzgreenmusic.com" className="flex items-center gap-2 text-white hover:text-[rgb(218,255,1)] transition-colors text-sm font-medium">
                    <Mail size={14} />
                    leaver@itzgreenmusic.com
                  </a>
                </div>
                
                <div>
                  <label className="text-[rgb(100,100,110)] text-[10px] tracking-wider block mb-1">LOCATION</label>
                  <div className="flex items-center gap-2 text-white text-sm font-medium">
                    <MapPin size={14} />
                    Hoboken, NJ
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 text-center">
              <p className="text-[rgb(161,161,170)] text-xs mb-4">
                Ready to submit your catalog?
              </p>
              <Link
                to="/ingestion"
                className="block w-full bg-[rgb(63,63,63)] text-white font-semibold text-xs tracking-[0.1em] py-3 rounded-lg hover:bg-[rgb(80,80,85)] transition-colors"
              >
                OPEN INGESTION PORTAL
              </Link>
            </div>
          </motion.div>
        </div>

        <Separator className="bg-[rgb(63,63,63)] mb-8" />

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-[10px] text-[rgb(100,100,110)] tracking-wide">
          <p>© 2026 ItzGreen Music Publishing. All Rights Reserved.</p>
          <div className="flex gap-4">
            <Link to="/ingestion" className="hover:text-white transition-colors">Catalog Submissions</Link>
            <span>All music pre-cleared for global sync licensing.</span>
          </div>
        </div>

      </div>
    </main>
  );
};

export default IntakePage;
